import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { adminAxios, getOrderPin, setOrderPin, formatPence } from './ordering/orderingApi';
import usePageTitle from '@/hooks/usePageTitle';

export default function StaffOrderPage() {
  usePageTitle('Staff Order');
  const [authed, setAuthed] = useState(!!getOrderPin());
  const [pinInput, setPinInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [seat, setSeat] = useState('');
  const [method, setMethod] = useState('cash');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!authed) return;
    Promise.all([
      adminAxios().get('/admin/order/categories'),
      adminAxios().get('/admin/order/products'),
    ])
      .then(([c, p]) => {
        setCategories(c.data.categories || []);
        setProducts(p.data.products || []);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          setOrderPin('');
          setAuthed(false);
        }
      });
  }, [authed]);

  const productsByCat = useMemo(() => {
    const out = {};
    categories.forEach((c) => { out[c.id] = products.filter((p) => p.category_id === c.id); });
    return out;
  }, [categories, products]);

  const cartLines = useMemo(() => {
    const lines = [];
    Object.entries(cart).forEach(([pid, info]) => {
      if (!info?.qty) return;
      const p = products.find((x) => x.id === pid);
      if (!p) return;
      let unit = p.price_pence;
      const chosen = [];
      (p.options || []).forEach((g) => {
        const cid = info.choices?.[g.id];
        if (cid) {
          const c = (g.choices || []).find((x) => x.id === cid);
          if (c) { unit += c.price_delta_pence || 0; chosen.push(`${g.name}: ${c.name}`); }
        }
      });
      lines.push({ pid, name: p.name, qty: info.qty, unit, line: unit * info.qty, chosen });
    });
    return lines;
  }, [cart, products]);

  const total = cartLines.reduce((s, l) => s + l.line, 0);

  const inc = (pid, d) => setCart((prev) => ({ ...prev, [pid]: { ...(prev[pid] || { qty: 0, choices: {} }), qty: Math.max(0, (prev[pid]?.qty || 0) + d) } }));
  const choose = (pid, gid, cid) => setCart((prev) => ({ ...prev, [pid]: { ...(prev[pid] || { qty: 0, choices: {} }), choices: { ...(prev[pid]?.choices || {}), [gid]: cid } } }));

  const submit = async () => {
    if (!first.trim() || !seat.trim()) return toast.error('Name and seat required');
    if (cartLines.length === 0) return toast.error('Add items first');
    for (const line of cartLines) {
      const p = products.find((x) => x.id === line.pid);
      for (const g of p.options || []) {
        if (g.required && !cart[line.pid]?.choices?.[g.id]) return toast.error(`Choose ${g.name} for ${p.name}`);
      }
    }
    setSubmitting(true);
    try {
      const items = cartLines.map((l) => ({
        product_id: l.pid,
        quantity: l.qty,
        choices: Object.entries(cart[l.pid]?.choices || {}).map(([gid, cid]) => ({ option_group_id: gid, choice_id: cid })),
      }));
      await adminAxios().post('/admin/order/staff-create', {
        first_name: first.trim(),
        last_name: last.trim(),
        seat_number: seat.trim(),
        payment_method: method,
        items,
      });
      toast.success('Order created');
      setCart({}); setFirst(''); setLast(''); setSeat('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <form onSubmit={(e) => { e.preventDefault(); setOrderPin(pinInput); setAuthed(true); }} className="bg-white rounded-2xl p-8 shadow w-80 space-y-3">
          <h1 className="font-bold text-xl">Staff Order</h1>
          <input type="password" value={pinInput} onChange={(e) => setPinInput(e.target.value)} placeholder="Admin PIN" className="w-full px-3 py-2 border rounded-lg" />
          <button className="w-full py-2 bg-black text-white rounded-lg font-semibold">Enter</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <h1 className="font-bold text-xl">Staff Order</h1>
        <button onClick={() => { setOrderPin(''); setAuthed(false); }} className="text-xs underline opacity-70">Logout</button>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <section className="bg-white rounded-xl p-4 shadow-sm space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" className="px-3 py-2 border rounded-lg" data-testid="staff-first" />
            <input value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" className="px-3 py-2 border rounded-lg" data-testid="staff-last" />
            <input value={seat} onChange={(e) => setSeat(e.target.value.toUpperCase())} placeholder="Seat" className="px-3 py-2 border rounded-lg" data-testid="staff-seat" />
          </div>
          <div className="flex gap-2">
            {['cash', 'comp', 'card_in_person'].map((m) => (
              <button key={m} onClick={() => setMethod(m)} className={`flex-1 py-2 rounded-lg text-sm border ${method === m ? 'bg-black text-white border-black' : 'bg-white'}`} data-testid={`staff-method-${m}`}>
                {m === 'card_in_person' ? 'Card (in person)' : m === 'comp' ? 'Comp' : 'Cash'}
              </button>
            ))}
          </div>
        </section>

        {categories.map((c) => (
          <section key={c.id}>
            <h2 className="font-bold mb-2">{c.name}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(productsByCat[c.id] || []).map((p) => {
                const info = cart[p.id] || { qty: 0, choices: {} };
                return (
                  <div key={p.id} className="bg-white rounded-xl p-3 shadow-sm flex flex-col">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-gray-500">{formatPence(p.price_pence)}</div>
                    <div className="flex items-center justify-end gap-2 mt-2">
                      <button onClick={() => inc(p.id, -1)} className="w-8 h-8 rounded-full bg-gray-200">−</button>
                      <span className="w-6 text-center font-bold" data-testid={`staff-qty-${p.id}`}>{info.qty}</span>
                      <button onClick={() => inc(p.id, +1)} className="w-8 h-8 rounded-full bg-black text-white" data-testid={`staff-add-${p.id}`}>+</button>
                    </div>
                    {info.qty > 0 && (p.options || []).map((g) => (
                      <div key={g.id} className="mt-2">
                        <div className="text-xs font-semibold">{g.name}{g.required && ' *'}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(g.choices || []).map((opt) => {
                            const active = info.choices?.[g.id] === opt.id;
                            return (
                              <button key={opt.id} onClick={() => choose(p.id, g.id, opt.id)} className={`text-xs px-2 py-1 rounded-full border ${active ? 'bg-black text-white border-black' : 'bg-white'}`}>
                                {opt.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </main>
      <div className="fixed bottom-0 left-0 right-0 bg-black text-white px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-xs opacity-70">Total</div>
          <div className="text-xl font-bold" data-testid="staff-total">{formatPence(total)}</div>
        </div>
        <button onClick={submit} disabled={submitting || cartLines.length === 0} className="bg-green-600 px-6 py-3 rounded-xl font-bold disabled:opacity-50" data-testid="staff-submit">
          {submitting ? 'Creating…' : 'Send to kitchen'}
        </button>
      </div>
    </div>
  );
}
